import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Minus, Plus, ShoppingBag, ArrowLeft, Lock, Mail } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { createCheckout, saveCart } from '../lib/api';
import { activeSale, withSale } from '../lib/sale';
import Reveal from '../components/Reveal';
import FreeShipBar from '../components/FreeShipBar';

const Cart = () => {
  const { items, updateQuantity, removeItem, subtotal, count, discount, braidQty, freeAwayCount } = useCart();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [savingCart, setSavingCart] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveErr, setSaveErr] = useState('');
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const handleSaveCart = async () => {
    setSaveErr('');
    if (!email || !email.includes('@')) { setSaveErr('Please enter a valid email.'); return; }
    setSavingCart(true);
    try {
      await saveCart(email, items);
      setSaved(true);
    } catch (e) {
      setSaveErr(e.message || 'Could not save your bag.');
    } finally {
      setSavingCart(false);
    }
  };

  const salePrice = (i) => withSale(i.price, activeSale(i));
  const saleSubtotal = items.reduce((s, i) => s + salePrice(i) * i.quantity, 0);
  const savings = subtotal - saleSubtotal;
  const shipping = subtotal > 0 ? 9.99 : 0;
  const total = Math.max(0, saleSubtotal - discount + shipping);

  const startCheckout = async () => {
    setLoading(true);
    setError('');
    try {
      const payload = items.map((i) => ({ id: i.id, quantity: i.quantity, length: i.length, color: i.color, density: i.density }));
      const { checkout_url } = await createCheckout(payload, window.location.origin);
      window.location.href = checkout_url;
    } catch (e) {
      setError('Could not start checkout. Please try again.');
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 py-28 text-center">
        <ShoppingBag size={48} className="text-[#c6a15b] mx-auto mb-6" />
        <h1 className="font-display text-5xl text-[#1a1a1a] mb-3">Your bag is empty</h1>
        <p className="font-body text-[#6b6459] mb-8">Looks like you haven’t added anything yet.</p>
        <Link to="/shop" className="btn-gold inline-block px-10 py-4"><span>Start Shopping</span></Link>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-12">
      <div className="flex items-end justify-between mb-10">
        <div>
          <p className="overline text-[#a8823c] mb-2">{count} items</p>
          <h1 className="font-display text-5xl text-[#1a1a1a]">Shopping Bag</h1>
        </div>
        <Link to="/shop" className="hidden sm:flex items-center gap-2 overline text-[0.65rem] text-[#6b6459] hover:text-[#a8823c]"><ArrowLeft size={15} /> Continue Shopping</Link>
      </div>

      <div className="grid lg:grid-cols-3 gap-10">
        {/* Items */}
        <Reveal className="lg:col-span-2">
        <div>
          <div className="hidden sm:grid grid-cols-12 gap-4 pb-4 border-b border-[#e6ddcd] overline text-[0.6rem] text-[#6b6459]">
            <span className="col-span-6">Product</span>
            <span className="col-span-2 text-center">Price</span>
            <span className="col-span-2 text-center">Quantity</span>
            <span className="col-span-2 text-right">Total</span>
          </div>
          {items.map((item) => (
            <div key={item.key} className="grid grid-cols-12 gap-4 items-center py-6 border-b border-[#e6ddcd]">
              <div className="col-span-12 sm:col-span-6 flex gap-4">
                <img src={item.image} alt={item.name} className="w-20 h-24 object-cover bg-[#ece5d8]" />
                <div>
                  <h3 className="font-display text-xl text-[#1a1a1a] leading-tight">{item.name}</h3>
                  <p className="font-body text-xs text-[#6b6459] mt-1">{[item.length, item.density, item.color].filter(Boolean).join(' · ')}</p>
                  <button onClick={() => removeItem(item.key)} className="flex items-center gap-1 font-body text-xs text-[#a39c8e] hover:text-[#0b0b0b] mt-2"><X size={13} /> Remove</button>
                </div>
              </div>
              <div className="col-span-4 sm:col-span-2 text-left sm:text-center font-body text-sm">
                {activeSale(item) ? (
                  <span className="flex sm:flex-col items-baseline sm:items-center gap-1.5 sm:gap-0">
                    <span className="text-[#a39c8e] line-through text-xs">${item.price.toFixed(2)}</span>
                    <span className="text-emerald-600 font-semibold">${salePrice(item).toFixed(2)}</span>
                  </span>
                ) : (
                  <span className="text-[#4a463d]">${item.price.toFixed(2)}</span>
                )}
              </div>
              <div className="col-span-4 sm:col-span-2 flex sm:justify-center">
                <div className="flex items-center border border-[#d8cdb8]">
                  <button onClick={() => updateQuantity(item.key, item.quantity - 1)} className="px-2.5 py-1.5 text-[#6b6459] hover:text-[#0b0b0b]"><Minus size={13} /></button>
                  <span className="px-3 font-body text-sm">{item.quantity}</span>
                  <button onClick={() => updateQuantity(item.key, item.quantity + 1)} className="px-2.5 py-1.5 text-[#6b6459] hover:text-[#0b0b0b]"><Plus size={13} /></button>
                </div>
              </div>
              <div className="col-span-4 sm:col-span-2 text-right font-body">
                {activeSale(item) ? (
                  <span className="flex flex-col items-end">
                    <span className="text-[#a39c8e] line-through text-xs">${(item.price * item.quantity).toFixed(2)}</span>
                    <span className="text-emerald-600 font-semibold">${(salePrice(item) * item.quantity).toFixed(2)}</span>
                  </span>
                ) : (
                  <span className="text-[#a8823c]">${(item.price * item.quantity).toFixed(2)}</span>
                )}
              </div>
            </div>
          ))}
        </div>
        </Reveal>

        {/* Summary */}
        <Reveal className="lg:col-span-1" delay={0.12}>
        <div>
          <div className="bg-[#0b0b0b] p-8 sticky top-28">
            <h2 className="font-display text-3xl text-[#f7f3ec] mb-6">Order Summary</h2>
            <FreeShipBar dark />
            <div className="space-y-4 font-body text-sm mt-5">
              <div className="flex justify-between text-[#cfc7b8]"><span>Subtotal</span><span>${saleSubtotal.toFixed(2)}</span></div>
              {savings > 0.005 && (
                <p className="font-body text-xs text-emerald-400" data-testid="cart-sale-savings">You saved ${savings.toFixed(2)} with the Collection Sale.</p>
              )}
              {freeAwayCount > 0 && (
                <p className="font-body text-xs text-[#e7d3a1]" data-testid="cart-braid-nudge">
                  Add {freeAwayCount} more braiding pack{freeAwayCount > 1 ? 's' : ''} to get 1 FREE (Buy 4, Get 1 Free).
                </p>
              )}
              {discount > 0 && (
                <div className="flex justify-between text-[#e7d3a1]" data-testid="cart-braid-discount">
                  <span>Buy 4, Get 1 Free</span><span>−${discount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-[#cfc7b8]"><span>Shipping</span><span>${shipping.toFixed(2)}</span></div>
              <div className="gold-line my-2" />
              <div className="flex justify-between text-[#f7f3ec] text-lg"><span className="font-display">Total</span><span className="font-display text-[#e7d3a1]">${total.toFixed(2)}</span></div>
            </div>
            <button onClick={startCheckout} disabled={loading || items.length === 0}
              data-testid="cart-checkout-btn"
              className="btn-gold w-full py-4 flex items-center justify-center gap-2 mt-7 disabled:opacity-60">
              <span className="flex items-center gap-2"><Lock size={14} /> {loading ? 'Redirecting…' : 'Secure Checkout'}</span>
            </button>
            {error && <p className="font-body text-xs text-red-400 text-center mt-3" data-testid="cart-error">{error}</p>}
            <p className="font-body text-xs text-[#7a7365] text-center mt-4">Flat $9.99 U.S. shipping · taxes calculated at checkout.</p>

            {/* Save your bag — abandoned cart reminder opt-in */}
            <div className="mt-6 pt-6 border-t border-[#c6a15b]/20" data-testid="save-cart">
              {saved ? (
                <p className="font-body text-xs text-[#e7d3a1] text-center flex items-center justify-center gap-1.5" data-testid="save-cart-success">
                  <Mail size={13} className="text-[#c6a15b]" /> Your bag is saved — we'll send a gentle reminder.
                </p>
              ) : (
                <>
                  <p className="font-body text-xs text-[#cfc7b8] mb-2 flex items-center gap-1.5">
                    <Mail size={13} className="text-[#c6a15b]" /> Not ready? Save your bag and we'll email you a reminder.
                  </p>
                  <div className="flex gap-2">
                    <input
                      type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@email.com" data-testid="save-cart-email"
                      className="flex-1 bg-transparent border border-[#c6a15b]/40 focus:border-[#c6a15b] outline-none text-[#f7f3ec] font-body text-sm px-3 py-2.5 placeholder:text-[#7a7365]"
                    />
                    <button onClick={handleSaveCart} disabled={savingCart}
                      data-testid="save-cart-btn"
                      className="border border-[#c6a15b] text-[#e7d3a1] font-body text-xs uppercase tracking-wider px-4 py-2.5 hover:bg-[#c6a15b] hover:text-[#0b0b0b] transition-colors disabled:opacity-60">
                      {savingCart ? 'Saving…' : 'Save Bag'}
                    </button>
                  </div>
                  {saveErr && <p className="font-body text-xs text-red-400 mt-2" data-testid="save-cart-error">{saveErr}</p>}
                </>
              )}
            </div>
          </div>
        </div>
        </Reveal>
      </div>
    </div>
  );
};

export default Cart;
